import { useState, useEffect } from 'react';
import { isEqual } from 'lodash';

import {
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';

import { assignDocumentToAccountant, getOwnerUsers } from '../services/services';
import { Colors } from '../config/default';
import { docAssignment, userRoles } from '../constants/appConstants';
import { useToast } from '../toast/toastContext';
import Btn from './button';

// `assignedAccountantEmails` seeds the selection with whoever currently holds the
// document, so the modal opens on the real state rather than an empty box — and
// so UNCHECKING everybody is an unassign. Tier B has always taken an empty list
// as exactly that (documentV2.assignDocumentToAccountant); the Assign button
// being disabled on an empty selection was the only thing making it unreachable.
//
// `onAssigned` is the documents-list refetch (the home page's allQueuesData,
// threaded through iconColumn -> muiModel). Called ONLY after the API reports
// success: the row must never show an assignee the server did not accept, which
// is also why nothing here writes the row optimistically.
const AssignDoc = ({ documentId, setOpen, assignedAccountantEmails = [], onAssigned }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [accountantsList, setAccountantsList] = useState([]);
  const currentAssignees = Array.isArray(assignedAccountantEmails)
    ? assignedAccountantEmails.filter(Boolean)
    : [];
  const [docAssignees, setDocAssignees] = useState(currentAssignees);
  const { showToast } = useToast();
  const { ASSIGN_DOC, SELECT_USERS } = docAssignment;
  const { ACCOUNTANT } = userRoles;

  const getUsers = async () => {
    const result = await getOwnerUsers();
    if (result?.status === 200) {
      const filteredUsers = result?.data?.payload?.data?.filter((item) => {
        return item?.role === ACCOUNTANT && item?.signUp === true;
      });
      const emails = filteredUsers?.map((item) => item?.email);
      setAccountantsList(emails);
    }
  };
  useEffect(() => {
    getUsers();
  }, []);

  // The modal is unmounted while closed, so this is really for the case where a
  // refetch changes the row underneath an open one.
  useEffect(() => {
    setDocAssignees(currentAssignees);
  }, [currentAssignees.join('|')]);

  const assignDocument = async () => {
    setIsLoading(true);
    const result = await assignDocumentToAccountant(documentId, docAssignees);
    if (result?.data?.metadata?.status === 'SUCCESS') {
      setOpen(false);
      setDocAssignees([]);
      setIsLoading(false);
      showToast(result?.data?.metadata?.message, 'success');
      // Refetch the list so the Assigned To column shows the new assignee — or
      // clears, on an unassign — without a page reload. After the toast, so a
      // failing refetch cannot swallow the success message, and never on the
      // error branch: a failed assign must leave the row exactly as it was.
      await onAssigned?.();
    } else {
      setIsLoading(false);
      showToast(
        result?.data?.metadata?.message || result?.response?.data?.metadata?.message,
        'error'
      );
    }
  };

  // The Assign button is disabled only while the request is in flight or when
  // the selection is what it already was. Emptiness alone must NOT disable it:
  // an empty selection is how a document is unassigned.
  const nothingChanged = isEqual([...docAssignees].sort(), [...currentAssignees].sort());

  const handleDocAssign = (event) => {
    setDocAssignees(event?.target?.value);
  };
  return (
    <Box sx={{ maxWidth: 550, margin: 'auto' }}>
      <Typography
        variant="h6"
        sx={{ color: Colors.WHITE, marginBottom: '1em', textAlign: 'center' }}>
        {ASSIGN_DOC}
      </Typography>
      <Grid
        container
        spacing={1}
        sx={{
          borderRadius: '10px',
          padding: '1em',
          width: '100%',
          marginLeft: '0px'
        }}>
        <FormControl
          fullWidth
          sx={{
            mt: 1,
            minWidth: '100%'
          }}>
          <InputLabel id="select-accountants-label" sx={{ fontSize: '16px' }}>
            {SELECT_USERS}
          </InputLabel>
          <Select
            labelId="select-accountants-label"
            id="select-accountants"
            multiple
            value={docAssignees}
            onChange={handleDocAssign}
            input={<OutlinedInput label="Select Accountants" />}
            renderValue={(selected) => (Array.isArray(selected) ? selected.join(', ') : '')}
            sx={{
              fontSize: '13px'
            }}>
            {accountantsList?.map((email) => (
              <MenuItem key={email} value={email}>
                <Checkbox checked={docAssignees?.includes(email)} />
                <ListItemText primary={email} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Btn
          text="Assign"
          width="7rem"
          color={Colors.WHITE}
          onClick={assignDocument}
          height="2.5rem"
          backgroundColor={Colors.DEEPBLUE}
          radius="10px"
          loading={isLoading}
          disabled={isLoading || nothingChanged}
          disabledColor={Colors.NAV_BLACK}
        />
      </Box>
    </Box>
  );
};

export default AssignDoc;
